import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Users, Send, UserPlus, Copy, Check, MessageSquare, ShieldCheck, Activity,
  LayoutGrid, HeartPulse,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /patient/care-circle — Blueprint Decision #33.
 *
 * Invite-only group chat. The patient owns the circle; family members
 * join via magic-link invite; assigned clinicians are auto-added when
 * they have an active appointment with this patient.
 */
export default function CareCircle() {
  const { user, getAuthHeader } = useAuth();
  const [circle, setCircle] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', display_name: '', relationship: 'family' });
  const [lastInviteUrl, setLastInviteUrl] = useState(null);
  const [copiedInvite, setCopiedInvite] = useState(false);
  const endRef = useRef(null);

  const patientId = user?.role === 'patient' ? user.id : null;

  const loadCircle = useCallback(async () => {
    if (!patientId) return;
    try {
      const { data } = await axios.get(`${API}/patient/${patientId}/care-circle`, getAuthHeader());
      setCircle(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load circle');
    } finally {
      setLoading(false);
    }
  }, [patientId, getAuthHeader]);

  const loadMessages = useCallback(async () => {
    if (!circle?.id) return;
    try {
      const { data } = await axios.get(`${API}/care-circle/${circle.id}/messages`, getAuthHeader());
      setMessages(data.messages || []);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
    } catch (e) { /* ignore */ }
  }, [circle?.id, getAuthHeader]);

  useEffect(() => { loadCircle(); }, [loadCircle]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll messages every 6 seconds — V1, switch to websocket later.
  useEffect(() => {
    if (!circle?.id) return undefined;
    const i = setInterval(loadMessages, 6000);
    return () => clearInterval(i);
  }, [circle?.id, loadMessages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim() || !circle?.id) return;
    setSending(true);
    try {
      await axios.post(`${API}/care-circle/${circle.id}/messages`, { body: draft }, getAuthHeader());
      setDraft('');
      await loadMessages();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const invite = async () => {
    if (!inviteForm.email || !inviteForm.display_name) {
      toast.error('Email and display name are required.');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/patient/${patientId}/care-circle/invite`, inviteForm, getAuthHeader());
      const url = `${window.location.origin}${data.public_url}`;
      setLastInviteUrl(url);
      setInviteForm({ email: '', display_name: '', relationship: 'family' });
      toast.success('Invite created. Share the link with your family member.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invite failed');
    }
  };

  const copyInvite = async () => {
    if (!lastInviteUrl) return;
    try {
      await navigator.clipboard.writeText(lastInviteUrl);
      setCopiedInvite(true);
      setTimeout(() => setCopiedInvite(false), 1800);
    } catch (e) { /* ignore */ }
  };

  const navItems = [
    { icon: Users, label: 'Care Circle', active: true },
    { icon: HeartPulse, label: 'My Care Plan', href: '/patient/care-plan' },
    { icon: LayoutGrid, label: 'Dashboard', href: '/patient/dashboard' },
    { icon: ShieldCheck, label: 'Trust Portal', href: '/trust' },
  ];

  const myUid = user?.id;

  return (
    <DashboardShell
      navItems={navItems}
      title="Care Circle"
      subtitle="A private group chat with your family and your assigned clinicians. Only invited members can read or post."
      testId="care-circle-page"
      rightSlot={
        user?.role === 'patient' && (
          <Button onClick={() => setShowInvite((s) => !s)} className="cn-btn cn-btn-primary" data-testid="care-circle-invite-btn">
            <UserPlus className="w-4 h-4" />
            {showInvite ? 'Hide invite' : 'Invite family'}
          </Button>
        )
      }
    >
      {loading ? (
        <div className="cn-card h-72 animate-pulse" />
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          {/* LEFT — CHAT */}
          <div className="lg:col-span-8">
            <div className="cn-card overflow-hidden">
              <div className="px-5 py-3 border-b border-cn-border bg-cn-bone-100/30 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-cn-primary" />
                <span className="font-semibold text-sm text-cn-text">Conversation</span>
              </div>
              <div className="p-5 h-[480px] overflow-y-auto space-y-3" data-testid="care-circle-messages">
                {messages.length === 0 ? (
                  <p className="text-sm text-cn-text-muted text-center py-12" data-testid="care-circle-empty">
                    No messages yet. Say hello to start the conversation.
                  </p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === myUid;
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`care-circle-msg-${m.id}`}>
                        <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? 'bg-cn-primary text-white' : 'bg-cn-bone-100 text-cn-text'}`}>
                          {!mine && (
                            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">
                              {m.sender_display_name} · {m.sender_role}
                            </div>
                          )}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
                          <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-cn-text-muted'}`}>
                            {new Date(m.sent_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-cn-border p-4 flex gap-2" data-testid="care-circle-composer">
                <input
                  className="cn-input flex-1"
                  placeholder="Write a message…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={2000}
                  data-testid="care-circle-draft"
                />
                <Button type="submit" disabled={sending || !draft.trim()} className="cn-btn cn-btn-primary" data-testid="care-circle-send">
                  <Send className="w-4 h-4" />
                  Send
                </Button>
              </form>
            </div>
          </div>

          {/* RIGHT — MEMBERS + INVITE */}
          <div className="lg:col-span-4">
            <div className="cn-card p-5 mb-4" data-testid="care-circle-members">
              <h3 className="font-display font-semibold text-cn-text dark:text-white mb-3 inline-flex items-center gap-2">
                <Users className="w-4 h-4 text-cn-primary" />
                Members ({circle?.members?.length || 0})
              </h3>
              <ul className="space-y-2">
                {(circle?.members || []).map((m) => (
                  <li key={m.user_id} className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-cn-bone-100/40" data-testid={`care-circle-member-${m.role}`}>
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      m.role === 'patient'   ? 'bg-cn-primary' :
                      m.role === 'clinician' ? 'bg-cn-sage-500' :
                                               'bg-cn-terracotta-500'
                    }`}>
                      {(m.display_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-cn-text truncate">{m.display_name}</div>
                      <div className="text-[11px] text-cn-text-muted capitalize">{m.role}{m.relationship ? ` · ${m.relationship}` : ''}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {showInvite && user?.role === 'patient' && (
              <div className="cn-card p-5 mb-4" data-testid="care-circle-invite-form">
                <h3 className="font-display font-semibold text-cn-text dark:text-white mb-3">Invite a family member</h3>
                <div className="space-y-3">
                  <input className="cn-input w-full text-sm" placeholder="Display name" value={inviteForm.display_name} onChange={(e) => setInviteForm((f) => ({ ...f, display_name: e.target.value }))} data-testid="invite-display-name" />
                  <input type="email" className="cn-input w-full text-sm" placeholder="Email address" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} data-testid="invite-email" />
                  <select className="cn-input w-full text-sm" value={inviteForm.relationship} onChange={(e) => setInviteForm((f) => ({ ...f, relationship: e.target.value }))} data-testid="invite-relationship">
                    <option value="family">Family</option>
                    <option value="spouse">Spouse / partner</option>
                    <option value="parent">Parent</option>
                    <option value="child">Adult child</option>
                    <option value="sibling">Sibling</option>
                    <option value="caregiver">Caregiver</option>
                    <option value="friend">Friend</option>
                  </select>
                  <Button onClick={invite} className="cn-btn cn-btn-primary w-full" data-testid="invite-submit">
                    <UserPlus className="w-4 h-4" /> Create invite link
                  </Button>
                </div>
                {lastInviteUrl && (
                  <div className="mt-3 rounded-xl bg-cn-primary/5 border border-cn-primary/20 p-3" data-testid="invite-result">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cn-primary mb-1">Magic-link invite</div>
                    <div className="flex items-center gap-2">
                      <code className="text-[11px] font-mono text-cn-text truncate flex-1">{lastInviteUrl}</code>
                      <button type="button" onClick={copyInvite} className="text-cn-primary" aria-label="Copy invite URL">
                        {copiedInvite ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-[10px] text-cn-text-muted mt-2">Share this link via text, email, or any messenger. It expires in 14 days.</p>
                  </div>
                )}
              </div>
            )}

            <p className="text-[11px] text-cn-text-muted px-1 inline-flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Messages auto-refresh every 6 seconds.
            </p>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
